import { ChecklistEvaluationKey } from './ChecklistEvaluations';
import type { EvaluationCoreDescriptor } from './ChecklistEvaluations';
import { FlagPurposeType } from './Flags';

export type SystemLossThresholdStatus = 'resolved' | 'noThresholdForState' | 'countyUnresolved';

export type SystemLossProductionModelStatus = 'extracted' | 'notFound';

export interface SystemLossDerivedRow {
  totalSystemLossPercent: number | null;
  snowLossPercent: number | null;
  expectedMinSystemLossPercent: number | null;
  expectedSnowLossPercent: number | null;
  locationLabel: string | null;
  thresholdStatus: SystemLossThresholdStatus;
  productionModelStatus: SystemLossProductionModelStatus;
}

// Mirrors the renderer's display rules so a flag reason reads the same as the card the reviewer saw.
const describeSystemLossRow = (row: Partial<SystemLossDerivedRow>): string => {
  const location = row.locationLabel ?? undefined;
  const forLocation = location ? ` for ${location}` : '';

  let reported: string;
  let expected: string | undefined;
  if (typeof row.totalSystemLossPercent === 'number') {
    reported = `Reported total system loss ${row.totalSystemLossPercent}%`;
    if (typeof row.expectedMinSystemLossPercent === 'number') {
      expected = `minimum ${row.expectedMinSystemLossPercent}%`;
    }
  } else if (typeof row.snowLossPercent === 'number') {
    reported = `Reported snow loss ${row.snowLossPercent}%`;
    if (typeof row.expectedSnowLossPercent === 'number') {
      expected = `expected ${row.expectedSnowLossPercent}%`;
    } else if (row.thresholdStatus === 'resolved' && typeof row.expectedMinSystemLossPercent === 'number') {
      expected = 'expected not applicable';
    }
  } else {
    return `System loss value not found in the Production Model${forLocation}`;
  }

  if (row.thresholdStatus === 'countyUnresolved') {
    return `${reported} (no expected value: county unresolved${forLocation})`;
  }
  if (row.thresholdStatus === 'noThresholdForState') {
    return `${reported} (no expected value: no threshold${forLocation})`;
  }
  return expected ? `${reported} vs ${expected}${forLocation}` : `${reported}${forLocation}`;
};

/**
 * The reviewer judges the reported loss against the published threshold through the
 * item's decision options, so every row passes once shown rather than auto-flagging.
 */
export const systemLossCore: EvaluationCoreDescriptor = {
  key: ChecklistEvaluationKey.systemLoss,
  label: 'System Loss',
  repeatable: false,
  supportsDecisionOptions: true,
  flagPurposeType: FlagPurposeType.additionalReview,
  defaultParams: {},
  fieldFormats: {
    totalSystemLossPercent: { label: 'Reported Total System Loss', unit: '%', decimals: 2 },
    snowLossPercent: { label: 'Reported Snow Loss', unit: '%', decimals: 2 },
    expectedMinSystemLossPercent: { label: 'Minimum System Loss', unit: '%', decimals: 1 },
    expectedSnowLossPercent: { label: 'Expected Snow Loss', unit: '%', decimals: 1 },
    locationLabel: { label: 'Location' },
    thresholdStatus: { label: 'Threshold Status' },
    productionModelStatus: { label: 'Production Model Status' },
  },
  verdictMessages: {
    pass: 'Reported loss shown for reviewer decision — no automatic flag',
    incomplete: 'Data unavailable',
  },
  computeRow: () => ({ verdict: 'pass' }),
  buildFlagReason: (rows) => {
    const derived = rows[0]?.derived as unknown as Partial<SystemLossDerivedRow> | undefined;
    return describeSystemLossRow(derived ?? {});
  },
};
